import { Box, Flex, Text } from "@chakra-ui/react";
import { FeedStreakMilestoneItem } from "./types";

interface FeedStreakCardProps {
  item: FeedStreakMilestoneItem;
  isActive: boolean;
}

/**
 * Daily-visit milestone celebration. Compact card that appears when a user's
 * streak crosses a threshold (3/7/14/30/...). FE-only — never sent by the
 * API. Tapping does nothing destructive; the card is its own reward.
 */
export function FeedStreakCard({ item, isActive }: FeedStreakCardProps) {
  return (
    <Flex
      align="center"
      gap={4}
      w="100%"
      h="100%"
      px={5}
      py={4}
      tabIndex={isActive ? 0 : -1}
      bgGradient="linear(135deg, rgba(255, 125, 42, 0.92) 0%, rgba(196, 38, 105, 0.92) 100%)"
      borderRadius="2xl"
      border="1px solid rgba(255,255,255,0.14)"
      boxShadow="0 8px 28px rgba(0,0,0,0.4)"
      color="white"
      position="relative"
      overflow="hidden"
    >
      {/* Soft halo behind the streak number so it reads cleanly over the
          gradient. */}
      <Box
        position="absolute"
        top="50%"
        left="40px"
        transform="translateY(-50%)"
        w="120px"
        h="120px"
        borderRadius="50%"
        bg="rgba(255,255,255,0.14)"
        filter="blur(22px)"
        pointerEvents="none"
      />
      <Flex align="baseline" gap={1} zIndex={1} flexShrink={0}>
        <Text fontSize="5xl" fontWeight="black" lineHeight="1">
          {item.streak}
        </Text>
        <Text fontSize="md" fontWeight="bold" opacity={0.85}>
          {item.streak === 1 ? "day" : "days"}
        </Text>
      </Flex>
      <Flex direction="column" zIndex={1} flex="1" minW="0">
        <Text
          fontSize="xs"
          fontWeight="extrabold"
          letterSpacing="0.16em"
          textTransform="uppercase"
          opacity={0.85}
        >
          Daily streak
        </Text>
        <Text fontSize="sm" fontWeight="semibold" opacity={0.95} noOfLines={2}>
          {milestoneCopy(item.milestone)}
        </Text>
      </Flex>
    </Flex>
  );
}

function milestoneCopy(milestone: number): string {
  if (milestone >= 100) return "100 days. You're built different.";
  if (milestone >= 50) return "Half a hundred. Locked in.";
  if (milestone >= 30) return "A month of comics. Big mood.";
  if (milestone >= 14) return "Two weeks strong. Keep it going.";
  if (milestone >= 7) return "A full week — that's a habit forming.";
  if (milestone >= 3) return "Three days in. Don't break the chain.";
  return "Welcome back — your streak starts now.";
}
